/* eslint-disable react/require-default-props */
/* eslint-disable import/no-cycle */
import React, { PureComponent } from 'react';
import ReactTooltip from 'react-tooltip';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ColorPickerIcon from '../ColorPickerIcon';
import ColorPicker from '../ColorPicker/ColorPicker';
import handleKeyPress from '../../common/handleKeyPress';
import handleDropdownKeyPress from '../../common/handleDropdownKeyPress';
import { COLORS } from '../../common/branding/colors';
import * as Styled from './styles';

export default class Button extends PureComponent {
    state = {
        isOver: false,
        colorPickerTopMargin: 115,
        ref: React.createRef(),
    };

    componentDidUpdate(prevProps, prevState) {
        const { hasFullStylingAccess, hasCustomizationFeature, presetColors, screenCheck } = this.props;
        const { isFocused } = prevProps;
        const { ref } = prevState;

        if (presetColors && presetColors !== prevProps.presetColors) {
            this._adjustColorPickerUI(hasFullStylingAccess, hasCustomizationFeature, screenCheck, presetColors);
        }

        if (isFocused && ref && ref.current) {
            ref.current.focus();
        }
    }

    /**
     * This function adjust the Color Picker container based on the size of the user's screen.
     * The container will be displayed either on top or bottom of the input depending on the size
     * of the screen
     * @param {bool} hasFullStylingAccess - Based on user's permission, will see the color picker differently
     * @param {bool} hasCustomizationFeature - Based on user's permission, will see the color picker differently
     * @param {bool} screenCheck
     * @param {number} presetColors - Based on the number of preset colors in the color picker, the margin changes
     *
     */
    _adjustColorPickerUI = (hasFullStylingAccess, hasCustomizationFeature, screenCheck, presetColors) => {
        const MARGIN_TOP = 115;
        const NO_STYLING_ACCESS_MARGIN_TOP = 44;
        const SMALL_COLOR_ROW_MARGIN = 13;
        const BIG_COLOR_ROW_MARGIN = 18;
        const OPPOSITE_DIRECTION_MARGIN_DIFF = 6;
        const MARGIN_OFFSET = 25;

        let margin;
        const smallBrowser = window.innerHeight < 845;
        const isRenderingInOppositeDirection = smallBrowser && screenCheck;

        if (!hasFullStylingAccess) {
            margin = NO_STYLING_ACCESS_MARGIN_TOP;
            if (presetColors.length > 5) {
                margin += BIG_COLOR_ROW_MARGIN;
            }

            this.setState({
                colorPickerTopMargin: isRenderingInOppositeDirection
                    ? -margin + OPPOSITE_DIRECTION_MARGIN_DIFF
                    : margin,
            });
            return;
        }

        margin = MARGIN_TOP;
        if (presetColors.length > 6) {
            margin += SMALL_COLOR_ROW_MARGIN;
        }

        if (!hasCustomizationFeature) {
            margin += isRenderingInOppositeDirection ? OPPOSITE_DIRECTION_MARGIN_DIFF : -MARGIN_OFFSET;
        }

        this.setState({
            colorPickerTopMargin: isRenderingInOppositeDirection
                ? -margin + OPPOSITE_DIRECTION_MARGIN_DIFF + MARGIN_OFFSET
                : margin,
        });
    };

    render() {
        const {
            value,
            type,
            width,
            label,
            icon,
            iconSize,
            iconPadding,
            iconAnimation,
            src,
            ariaPressed,
            onClick,
            onChangeComplete,
            onClose,
            onChange,
            hasHoverEffects,
            hasTooltip,
            shouldDisplayColorPicker,
            color,
            backgroundColor,
            onHoverBackgroundColor,
            onHoverColor,
            disabled,
            cursor,
            tooltipPosition,
            tooltipWidth,
            tooltipTextAlign,
            tooltipPadding,
            tooltipFontWeight,
            tooltipLineHeight,
            tooltipFontSize,
            marginBottom,
            padding,
            presetColors,
            hasFullStylingAccess,
            hasCustomizationFeature,
            hasCustomizationAccess,
            dataFor,
            dataTestId,
            ariaLabel,
            keyDownPreventDefault,
        } = this.props;
        const {
            isOver,
            colorPickerTopMargin,
            ref,
        } = this.state;

        const onCloseDropdown = (e) => {
            e.stopPropagation();
            onClose();
        };

        switch (type) {
            case 'simple':
                return (
                    <>
                        <div
                            data-tip
                            data-for={dataFor || label}
                            data-test-id={`${dataTestId || label}-button`}
                            style={{
                                width,
                                display: 'inline-block',
                                height: width,
                                position: 'relative',
                                cursor,
                                color: 'black',
                                backgroundColor: hasHoverEffects && isOver ? onHoverBackgroundColor : backgroundColor,
                                opacity: disabled ? 0.25 : 1,
                                pointerEvents: disabled ? 'none' : null,
                                marginBottom,
                            }}
                            onMouseOver={() => this.setState({ isOver: true })}
                            onMouseLeave={() => this.setState({ isOver: false })}
                            onFocus={() => this.setState({ isOver: true })}
                            onBlur={() => this.setState({ isOver: false })}
                        >
                            <div style={{ position: 'absolute', textAlign: 'center' }}>
                                <Styled.ButtonSimpleDiv
                                    width={width}
                                    padding={padding}
                                    cursor={cursor}
                                    hasHoverEffects={hasHoverEffects}
                                    isOver={isOver}
                                    onHoverBackgroundColor={onHoverBackgroundColor}
                                    backgroundColor={backgroundColor}
                                    onHoverColor={onHoverColor}
                                    role={onClick && 'button'}
                                    aria-label={ariaLabel || (onClick && label.replace('-', ' '))}
                                    aria-pressed={ariaPressed}
                                    onClick={onClick}
                                    onKeyDown={(e) => !disabled && onClick && handleKeyPress(e, () => onClick(e), keyDownPreventDefault)}
                                    tabIndex={0}
                                    ref={ref}
                                >
                                    {src ? (
                                        <img
                                            src={src}
                                            width={width}
                                            height={width}
                                            alt={label}
                                            style={{ padding, boxSizing: 'border-box' }}
                                        />
                                    ) : (
                                        <FontAwesomeIcon
                                            style={{
                                                color: hasHoverEffects && isOver ? onHoverColor : color,
                                                fontSize: iconSize,
                                                padding: iconPadding,
                                                animation: iconAnimation,
                                            }}
                                            icon={icon}
                                        />
                                    )}
                                </Styled.ButtonSimpleDiv>
                            </div>
                        </div>
                        {hasTooltip && (
                            <ReactTooltip
                                className="react-tooltip-text-wrapper"
                                id={label}
                                place={tooltipPosition}
                                effect="solid"
                            >
                                <div
                                    style={{
                                        width: tooltipWidth,
                                        textAlign: tooltipTextAlign,
                                        padding: tooltipPadding,
                                    }}
                                >
                                    <span data-multiline style={{
                                        fontWeight: tooltipFontWeight,
                                        lineHeight: tooltipLineHeight,
                                        fontSize: tooltipFontSize,
                                    }}>
                                        {label}
                                    </span>
                                </div>
                            </ReactTooltip>
                        )}
                    </>
                );

            case 'colorPicker':
                return (
                    <div
                        style={{
                            flexGrow: 1,
                            border: '1px solid #CCCCCC',
                            boxSizing: 'border-box',
                            display: 'flex',
                            alignItems: 'center',
                            maxWidth: 180,
                            minWidth: 180,
                            height: 28,
                            padding: 5,
                            width,
                            cursor: 'default',
                            color: '#727273',
                            opacity: disabled ? 0.25 : 1,
                            pointerEvents: disabled ? 'none' : null,
                            marginBottom,
                        }}
                        onClick={onClick}
                        onKeyDown={(e) =>
                            handleDropdownKeyPress(
                                e,
                                () => onClick(e),
                                () => onCloseDropdown(e)
                            )
                        }
                        tabIndex={0}
                        role="button"
                    >
                        <div style={{ width: 20, height: 20, minWidth: 20 }}>
                            <ColorPickerIcon fill={value} />
                        </div>
                        <label htmlFor={`button-${label}`}>
                            <input
                                style={{
                                    fontSize: 12,
                                    color: COLORS.neutral900,
                                    display: 'flex',
                                    alignItems: 'center',
                                    border: 0,
                                    outline: 0,
                                    marginLeft: 5,
                                    maxWidth: 121,
                                    minWidth: 121,
                                    cursor: 'default',
                                }}
                                id={`button-${label}`}
                                type="text"
                                value={value}
                                onChange={onChange}
                                readOnly
                            />
                        </label>
                        <i className="material-icons" style={{ fontSize: 20, color: '#727273' }}>
                            arrow_drop_down
                        </i>
                        {shouldDisplayColorPicker ? (
                            <div
                                style={{
                                    position: 'relative',
                                    zIndex: '3',
                                    top: colorPickerTopMargin,
                                    right: 174,
                                    marginLeft: 0,
                                }}
                            >
                                <div
                                    style={{ position: 'fixed', top: '0px', right: '0px', bottom: '0px', left: '0px' }}
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        onClose();
                                    }}
                                    onKeyDown={(e) => handleKeyPress(e, () => {
                                        e.stopPropagation();
                                        onClose();
                                    })}
                                    role="button"
                                    tabIndex={0}
                                />
                                <ColorPicker
                                    hasFullStylingAccess={hasFullStylingAccess}
                                    hasCustomizationFeature={hasCustomizationFeature}
                                    hasCustomizationAccess={hasCustomizationAccess}
                                    disableAlpha
                                    color={color}
                                    presetColors={presetColors}
                                    onChangeComplete={onChangeComplete}
                                    width={160}
                                />
                            </div>
                        ) : null}
                    </div>
                );
            default:
                return null;
        }
    }
}

Button.propTypes = {
    ariaPressed: PropTypes.bool,
    backgroundColor: PropTypes.string,
    color: PropTypes.string,
    onHoverBackgroundColor: PropTypes.string,
    onHoverColor: PropTypes.string,
    cursor: PropTypes.string,
    type: PropTypes.string.isRequired,
    width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    label: PropTypes.string.isRequired,
    icon: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
    iconSize: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    iconPadding: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    iconAnimation: PropTypes.string,
    src: PropTypes.string,
    onClick: PropTypes.func,
    screenCheck: PropTypes.bool,
    tooltipPosition: PropTypes.string,
    tooltipWidth: PropTypes.string,
    tooltipTextAlign: PropTypes.string,
    tooltipPadding: PropTypes.string,
    tooltipFontWeight: PropTypes.string,
    tooltipLineHeight: PropTypes.string,
    tooltipFontSize: PropTypes.string,
    marginBottom: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    padding: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    disabled: PropTypes.bool,
    onClose: PropTypes.func,
    onChangeComplete: PropTypes.func,
    onChange: PropTypes.func,
    hasHoverEffects: PropTypes.bool,
    hasTooltip: PropTypes.bool,
    shouldDisplayColorPicker: PropTypes.bool,
    value: PropTypes.string,
    presetColors: PropTypes.array,
    hasFullStylingAccess: PropTypes.bool,
    hasCustomizationFeature: PropTypes.bool,
    hasCustomizationAccess: PropTypes.bool,
    dataFor: PropTypes.string,
    dataTestId: PropTypes.string,
    ariaLabel: PropTypes.string,
    keyDownPreventDefault: PropTypes.bool,
    isFocused: PropTypes.bool,
};

Button.defaultProps = {
    cursor: 'pointer',
    backgroundColor: 'white',
    color: '#5A5858',
    onHoverBackgroundColor: COLORS.pastelPrimary300,
    onHoverColor: COLORS.primary300,
    marginBottom: 15,
    src: '',
    icon: '',
    iconSize: 20,
    iconPadding: 4,
    iconAnimation: '',
    padding: 0,
    tooltipPosition: 'bottom',
    tooltipWidth: 'auto',
    tooltipTextAlign: 'center',
    tooltipPadding: '0px',
    tooltipFontWeight: '700',
    tooltipLineHeight: 'normal',
    tooltipFontSize: '13px',
    screenCheck: false,
    hasHoverEffects: true,
    hasTooltip: true,
    hasCustomizationFeature: false,
    hasCustomizationAccess: false,
    ariaLabel: '',
    keyDownPreventDefault: false,
    isFocused: false,
};
